import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  DeleteOutline as ClearIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/ui';
import PrinterSelect from '../../components/print/PrinterSelect';
import PrintJobsPanel from '../../components/print/PrintJobsPanel';
import usePrinter from '../../hooks/usePrinter';
import { useNotify } from '../../components/NotifyProvider';
import { printClient } from '../../utils/printClient';
import {
  MS_BARCODE_FORMATS,
  readMsBarcodeFormat,
  readPrintAfterProduction,
  writeMsBarcodeFormat,
  writePrintAfterProduction,
} from '../../utils/printerState.mjs';

const PRINTER_NOT_SELECTED_MSG = 'Выберите принтер в Настройки → Печать';

export default function PrintSettings() {
  const printer = usePrinter();
  const selected = printer.selectedPrinter;
  const { success: notifySuccess, error: notifyError } = useNotify();
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [reprintingId, setReprintingId] = useState('');
  const [cancelingId, setCancelingId] = useState('');
  const [printAfterProduction, setPrintAfterProduction] = useState(() => readPrintAfterProduction());
  const [barcodeFormat, setBarcodeFormat] = useState(() => readMsBarcodeFormat());

  const loadJobs = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setJobsLoading(true);
    try {
      const data = await printClient.listJobs({ limit: 50 }, { silent: true });
      setJobs(data?.items || []);
    } catch (requestError) {
      if (!silent) {
        notifyError(requestError?.response?.data?.message || 'Не удалось загрузить задания печати');
      }
    } finally {
      if (!silent) setJobsLoading(false);
    }
  }, [notifyError]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    if (!jobs.some((job) => ['queued', 'leased', 'spooled'].includes(job.status))) {
      return undefined;
    }
    const timer = setInterval(() => loadJobs({ silent: true }), 3000);
    return () => clearInterval(timer);
  }, [jobs, loadJobs]);

  const reprintJob = async (job) => {
    if (!printer.selectedPrinter) {
      notifyError(PRINTER_NOT_SELECTED_MSG);
      return;
    }
    setReprintingId(String(job.id));
    try {
      await printClient.reprint(job.id, printer.selectedPrinter.printer_uid);
      notifySuccess('Успешно напечатано');
      await loadJobs();
    } catch (requestError) {
      notifyError(requestError?.response?.data?.message || 'Не удалось перепечатать');
      if (requestError?.response?.data?.code === 'printer_offline') await printer.refresh();
    } finally {
      setReprintingId('');
    }
  };

  const cancelJob = async (job) => {
    setCancelingId(String(job.id));
    try {
      await printClient.cancelJob(job.id);
      notifySuccess('Задание отменено');
      await loadJobs();
    } catch (requestError) {
      notifyError(requestError?.response?.data?.message || 'Не удалось отменить задание');
      await loadJobs({ silent: true });
    } finally {
      setCancelingId('');
    }
  };

  const openMassPdf = async (job) => {
    try {
      await printClient.openMassPdf(job.id);
    } catch (requestError) {
      notifyError(requestError?.response?.data?.message || 'Не удалось открыть PDF');
    }
  };

  return (
    <>
      <PageHeader
        title="Настройки печати"
        subtitle="Принтер для стикеров заказов и QR поставок WB"
        breadcrumbs={[
          { label: 'Главная', path: '/' },
          { label: 'Настройки' },
          { label: 'Печать' },
        ]}
        actions={(
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              printer.refresh();
              loadJobs();
            }}
            disabled={printer.loading || jobsLoading}
          >
            Обновить
          </Button>
        )}
      />

      <Stack spacing={2}>
        <Alert severity="info">
          Выбор сохраняется в этом браузере на текущем рабочем месте. Страница
          сборки WB FBS использует выбранный здесь принтер автоматически.
        </Alert>

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, maxWidth: 760 }}>
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PrintIcon color="primary" />
              <Typography variant="titleLarge">Рабочий принтер</Typography>
            </Stack>

            <PrinterSelect
              printers={printer.printers}
              selectedPrinter={selected}
              storedPrinter={printer.storedPrinter}
              unavailable={printer.unavailable}
              loading={printer.loading}
              error={printer.error}
              onChange={printer.selectPrinter}
              fullWidth
            />

            {selected && (
              <>
                <Divider />
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="bodyMedium" fontWeight={700}>
                      {selected.name}
                    </Typography>
                    <Chip size="small" color="success" label="Online" />
                  </Stack>
                  <Typography variant="bodySmall" color="text.secondary">
                    Драйвер: {selected.driver_name || '—'}
                  </Typography>
                  <Typography variant="bodySmall" color="text.secondary">
                    Порт: {selected.port_name || '—'}
                  </Typography>
                  <Typography variant="bodySmall" color="text.secondary">
                    Агент: {selected.agent?.name || selected.agent?.hostname || '—'}
                  </Typography>
                </Stack>
              </>
            )}

            {!selected && printer.storedPrinter && (
              <Alert severity="warning">
                Сохранённый принтер сейчас недоступен. Выберите online-принтер или дождитесь агента.
              </Alert>
            )}

            {!selected && !printer.storedPrinter && (
              <Alert severity="warning">
                {PRINTER_NOT_SELECTED_MSG}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                color="inherit"
                startIcon={<ClearIcon />}
                onClick={printer.clearPrinter}
                disabled={!printer.storedPrinter}
              >
                Сбросить выбор
              </Button>
            </Stack>

            <Divider />
            <FormControlLabel
              control={(
                <Switch
                  checked={printAfterProduction}
                  onChange={(event) => {
                    const next = event.target.checked;
                    setPrintAfterProduction(writePrintAfterProduction(next));
                  }}
                />
              )}
              label="После производства печатать ШК товара"
            />
            <FormControl size="small" sx={{ maxWidth: 360 }}>
              <InputLabel id="ms-barcode-format-label">Формат этикетки ШК</InputLabel>
              <Select
                labelId="ms-barcode-format-label"
                label="Формат этикетки ШК"
                value={barcodeFormat}
                onChange={(event) => setBarcodeFormat(writeMsBarcodeFormat(event.target.value))}
              >
                {MS_BARCODE_FORMATS.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="bodySmall" color="text.secondary">
              На странице цеха после проведения этапа этикетка выбранного формата уйдёт на этот принтер.
              По умолчанию 58×40, печать выключена. Выбор хранится в этом браузере. Ошибка печати не откатывает этап.
            </Typography>
          </Stack>
        </Paper>

        <PrintJobsPanel
          jobs={jobs}
          loading={jobsLoading}
          onRefresh={() => loadJobs()}
          onReprint={reprintJob}
          onCancel={cancelJob}
          onOpenMassPdf={openMassPdf}
          reprintingId={reprintingId}
          cancelingId={cancelingId}
          canReprint
          title="Последние задания печати"
        />
      </Stack>
    </>
  );
}
